import { useState, useCallback } from 'react'
import { toast } from '../services/toastService'
import { contactAPI } from '../services/api'
import { validateEmail } from '../utils/validators'
import { trackContactFormSubmit, trackButtonClick } from '../utils/analytics'
import { createLogger } from '../utils/logger'

const logger = createLogger('useContactForm')

/**
 * Custom hook for managing contact form state and submission
 * Handles vendor-specific fields and form validation
 */
export function useContactForm(t) {
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    isVendor: false,
    vendorIntent: '',
    entityName: '',
    entityId: ''
  })
  
  const [contactErrors, setContactErrors] = useState({})
  const [isContactSubmitting, setIsContactSubmitting] = useState(false)
  const [contactSuccess, setContactSuccess] = useState(false)

  const handleContactChange = useCallback((e) => {
    const { name, value, type, checked } = e.target
    
    setContactFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error for this field when user starts typing
    if (contactErrors[name]) {
      setContactErrors(prev => ({ ...prev, [name]: '' }))
    }
    
    // If unchecking isVendor, clear vendor-specific fields
    if (name === 'isVendor' && !checked) {
      setContactFormData(prev => ({
        ...prev,
        vendorIntent: '',
        entityName: '',
        entityId: ''
      }))
    }
  }, [contactErrors])

  const validateContactForm = useCallback(() => {
    const errors = {}
    
    if (!contactFormData.name || contactFormData.name.trim() === '') {
      errors.name = t('contact.nameRequired')
    }
    
    if (!contactFormData.email || contactFormData.email.trim() === '') {
      errors.email = t('contact.emailRequired')
    } else if (!validateEmail(contactFormData.email, false)) {
      errors.email = t('contact.emailInvalid')
    }
    
    if (!contactFormData.subject || contactFormData.subject.trim() === '') {
      errors.subject = t('contact.subjectRequired')
    }
    
    if (!contactFormData.message || contactFormData.message.trim() === '') {
      errors.message = t('contact.messageRequired')
    } else if (contactFormData.message.trim().length < 10) {
      errors.message = t('contact.messageTooShort')
    }
    
    // Vendor-specific validation
    if (contactFormData.isVendor) {
      if (!contactFormData.vendorIntent || contactFormData.vendorIntent.trim() === '') {
        errors.vendorIntent = t('contact.vendorIntentRequired')
      }
    }
    
    setContactErrors(errors)
    return Object.keys(errors).length === 0
  }, [contactFormData, t])

  const handleContactSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    trackButtonClick('submit_contact_form', 'contact_tab', { 
      is_vendor: contactFormData.isVendor,
      has_subject: !!contactFormData.subject 
    })
    
    if (!validateContactForm()) {
      return
    }
    
    setIsContactSubmitting(true)
    
    try {
      const submissionData = {
        name: contactFormData.name.trim(),
        email: contactFormData.email.trim(),
        subject: contactFormData.subject && contactFormData.subject.trim() ? contactFormData.subject.trim() : null,
        message: contactFormData.message.trim(),
        isVendor: contactFormData.isVendor,
        vendorIntent: null,
        entityName: null,
        entityId: null
      }
      
      // Only include vendor-specific fields if isVendor is true
      if (contactFormData.isVendor) {
        submissionData.vendorIntent = contactFormData.vendorIntent && contactFormData.vendorIntent.trim() ? contactFormData.vendorIntent.trim() : null
        submissionData.entityName = contactFormData.entityName && contactFormData.entityName.trim() ? contactFormData.entityName.trim() : null
        
        // Only include entityId if it's a valid UUID format
        if (contactFormData.entityId && contactFormData.entityId.trim()) {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
          if (uuidRegex.test(contactFormData.entityId.trim())) {
            submissionData.entityId = contactFormData.entityId.trim()
          }
        }
      }
      
      await contactAPI.submitContact(submissionData)
      
      // Track analytics
      trackContactFormSubmit(
        contactFormData.isVendor,
        !!contactFormData.subject,
        'contact_tab',
        contactFormData.message.length
      )
      
      setContactSuccess(true)
      toast.success(t('contact.success'))
      
      // Reset form
      setContactFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        isVendor: false,
        vendorIntent: '',
        entityName: '',
        entityId: ''
      })
    } catch (error) {
      logger.error('Failed to submit contact form', error)
      toast.error(t('contact.error'))
    } finally {
      setIsContactSubmitting(false)
    }
  }, [contactFormData, validateContactForm, t])

  return {
    contactFormData,
    contactErrors,
    isContactSubmitting,
    contactSuccess,
    handleContactChange,
    validateContactForm,
    handleContactSubmit,
    setContactSuccess // Allow resetting success state
  }
}
